// Profili LED vuoti (redesign 2026-10, stesso stile di Profili su misura).
// Codice e prezzi vengono da looseProfileLogic.js (stessa logica di prima, verificata su 5000 configurazioni).
// Ogni parte (profilo, copertura, strip, clips, tappi, accessori) si aggiunge al carrello per conto suo,
// con le stesse righe di prima. Nuovo: scelte "confermate" / "predefinite" e controllo prima di aggiungere.
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../css/CatalogoPage.css';
import '../css/ShopPage.css';
import '../css/ConfiguratorPage.css';
import successSound from '../assets/success.mp3';
import { FiChevronLeft, FiCopy, FiShoppingCart, FiZoomIn } from 'react-icons/fi';
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import { AddedToast, PdfButton, QtyStepper, formatEuro } from './shopUi';
import {
  COVER_LENGTHS,
  PROFILE_LENGTHS,
  formatEuroPerMeter,
  generateLooseCode,
  getClipsTotal,
  getPricingSnapshot,
  getProfileMaterialPricePerMeter,
  getProfileStartingNetPricePerMeter,
  roundCurrency,
} from './looseProfileLogic';

const getGroupLabels = (t) => ({
  all: t('configurator.groupAll'),
  group1: t('configurator.groupSurface'),
  group2: t('configurator.groupRecessed'),
  group3: t('configurator.groupSuspended'),
});

const getProfileCardImage = (model) => model.cardImage || model.thumbImage || model.image;
// Foto per finitura (finishImages in configlooseprofiles.json), altrimenti la foto principale del modello.
const getFinishImage = (model, finish) => model?.finishImages?.[finish] || model?.image;

const FINISH_COLORS = { grigio: '#9AA1A8', nero: '#15181C', bianco: '#FFFFFF', champagne: '#D8C3A0' };

const LooseProfilesConfigurator = ({ showPrices = true }) => {
  const { addToCart, cart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const GROUP_LABELS = useMemo(() => getGroupLabels(t), [t]);
  const navigate = useNavigate();
  const discount = user?.discount || 0;

  const [configData, setConfigData] = useState({});
  const [stripLeds, setStripLeds] = useState([]);
  const [group, setGroup] = useState('all');
  const [view, setView] = useState('list');
  const [selectedModel, setSelectedModel] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [imageKind, setImageKind] = useState('photo');

  const [selectedColor, setSelectedColor] = useState('');
  const [length, setLength] = useState(100);
  const [profileQuantity, setProfileQuantity] = useState(1);
  const [selectedCoverType, setSelectedCoverType] = useState('');
  const [coverLength, setCoverLength] = useState(100);
  const [coverQuantity, setCoverQuantity] = useState(1);
  const [stripQuantities, setStripQuantities] = useState({});
  const [tappiQuantity, setTappiQuantity] = useState(0);
  const [clipsQuantity, setClipsQuantity] = useState(0);
  const [accessoryQuantities, setAccessoryQuantities] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [touched, setTouched] = useState(() => new Set());
  const [askFor, setAskFor] = useState(null); // 'profile' | 'cover'
  const [copied, setCopied] = useState(false);
  const stepRefs = useRef({});

  useEffect(() => {
    fetch('/api/products/config/configlooseprofiles')
      .then((response) => response.json())
      .then((rawData) => {
        const payload = rawData?.data || rawData;
        setConfigData(payload);
      })
      .catch((error) => console.error('Error loading configuration file:', error));

    fetch('/api/products/config/stripled')
      .then((response) => response.json())
      .then((data) => setStripLeds(Array.isArray(data) ? data : data?.data || []))
      .catch((error) => console.error('Error loading strip LED file:', error));
  }, []);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(''), 2400);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!warningMessage) return undefined;
    const timer = setTimeout(() => setWarningMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [warningMessage]);

  useEffect(() => {
    if (!zoomedImage) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoomedImage(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomedImage]);

  // Elenco modelli per gruppo (extraGroups: modello visibile anche in altri filtri)
  const models = useMemo(() => {
    const sourceModels = configData.models || {};
    if (group === 'all') {
      return Object.entries(sourceModels).flatMap(([groupKey, groupItems]) =>
        (groupItems || []).map((model) => ({ ...model, groupKey, groupLabel: GROUP_LABELS[groupKey] || groupKey })));
    }
    return [
      ...(sourceModels[group] || []),
      ...Object.entries(sourceModels)
        .filter(([groupKey]) => groupKey !== group)
        .flatMap(([, groupItems]) => (groupItems || []).filter((m) => m.extraGroups?.includes(group))),
    ].map((model) => ({ ...model, groupKey: group, groupLabel: GROUP_LABELS[group] || group }));
  }, [configData, group, GROUP_LABELS]);
  const groupCount = (key) => {
    const sourceModels = configData.models || {};
    if (key === 'all') return Object.values(sourceModels).flat().length;
    return (sourceModels[key] || []).length +
      Object.entries(sourceModels).filter(([g]) => g !== key).flatMap(([, l]) => l || []).filter((m) => m.extraGroups?.includes(key)).length;
  };

  const getColorLabel = (color) => {
    switch (color) {
      case 'nero':
        return t('looseProfiles.black');
      case 'bianco':
        return t('looseProfiles.white');
      case 'grigio':
        return t('looseProfiles.grey');
      default:
        return color ? color.charAt(0).toUpperCase() + color.slice(1) : '';
    }
  };

  // ---------- Opzioni del modello aperto ----------
  const availableFinishes = selectedModel?.availableFinishes || [];
  const availableProfileLengths = (selectedModel && configData.modelProfileLengthCompatibility?.[selectedModel.name]) || PROFILE_LENGTHS;
  const availableCovers = (selectedModel && configData.modelCoverCompatibility?.[selectedModel.name]) || [];
  const availableCoverLengths = (selectedModel && configData.modelCoverLengthCompatibility?.[selectedModel.name]) || COVER_LENGTHS;
  // Modelli senza cover vendibile a parte (es. BATTISCOPA): sezione cover nascosta, totale cover 0.
  const hasCover = availableCovers.length > 0;
  const clipsAvailable = selectedModel && configData.clipsPrices && configData.clipsPrices[selectedModel.name] > 0;
  const tappiAvailable = selectedModel && configData.tappiPrices && configData.tappiPrices[selectedModel.name] > 0;
  // Strip LED abbinabili al profilo (modelStripCompatibility = codici di stripled.json)
  const compatibleStripCodes = selectedModel ? configData.modelStripCompatibility?.[selectedModel.name] || [] : [];
  const compatibleStrips = stripLeds.filter((strip) => compatibleStripCodes.includes(strip.code));
  // Accessori specifici del modello; price null = visibile ma non ancora ordinabile.
  const modelAccessories = selectedModel ? configData.modelAccessories?.[selectedModel.name] || [] : [];
  // "prices" = costo per finitura (es. kit sospensione bianco/nero), altrimenti "price".
  const getAccessoryListPrice = (accessory) => {
    const cost = accessory.prices?.[selectedColor] ?? accessory.price;
    return cost == null ? null : roundCurrency(cost * 4.5);
  };

  const pricing = getPricingSnapshot(configData, {
    selectedModel, selectedColor, length, hasCover, selectedCoverType, coverLength,
    profileQuantity, coverQuantity, tappiQuantity, clipsQuantity, discount,
  });

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play().catch((error) => {
      console.error('Audio playback error:', error);
    });
  };

  const showMessage = (message) => {
    setWarningMessage('');
    setSuccessMessage(message);
  };

  const showWarningMessage = (message) => {
    setSuccessMessage('');
    setWarningMessage(message);
  };

  const handleDownload = (datasheet) => {
    if (!datasheet) return;
    // urlAsset punta a goooled.it (dominio diverso da b2b.goooled.com): l'attributo
    // download di un <a> viene ignorato cross-origin, quindi apriamo in una nuova
    // scheda per non far navigare via il B2B dalla pagina corrente.
    window.open(urlAsset(datasheet), '_blank', 'noopener,noreferrer');
  };

  const handleSelectModel = (model) => {
    const profileLengths = configData.modelProfileLengthCompatibility?.[model.name] || PROFILE_LENGTHS;
    const covers = configData.modelCoverCompatibility?.[model.name] || [];
    const coverLengths = configData.modelCoverLengthCompatibility?.[model.name] || COVER_LENGTHS;
    setSelectedModel(model);
    setSelectedColor(model.availableFinishes?.[0] || '');
    setLength(profileLengths[0]);
    setProfileQuantity(1);
    setSelectedCoverType(covers[0] || '');
    setCoverLength(coverLengths[0] || COVER_LENGTHS[0]);
    setCoverQuantity(1);
    setTappiQuantity(0);
    setClipsQuantity(0);
    setStripQuantities({});
    setAccessoryQuantities({});
    setTouched(new Set());
    setAskFor(null);
    setImageKind('photo');
    setView('detail');
    window.scrollTo({ top: 0 });
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedModel(null);
  };

  const markTouched = (id) => {
    setTouched((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    setAskFor(null);
  };

  // ---------- Aggiunte al carrello (righe identiche a prima) ----------
  const handleAddProfile = () => {
    if (!selectedModel || !selectedColor || !length) {
      showWarningMessage(t('looseProfiles.warningSelectModelFinishLength'));
      return;
    }

    if (profileQuantity < 1) {
      showWarningMessage(t('looseProfiles.warningValidProfileQty'));
      return;
    }

    const model = selectedModel.name;
    const code = generateLooseCode(configData, model, selectedColor, length, '');
    const profilePricePerMeter = getProfileMaterialPricePerMeter(configData, model, selectedColor);

    if (profilePricePerMeter == null) {
      showWarningMessage(t('looseProfiles.warningProfilePriceUnavailable'));
      return;
    }

    const total = Math.round(profilePricePerMeter * (length / 100) * 4.5);
    const netPrice = roundCurrency(total * (1 - discount / 100));

    addToCart({
      code,
      description: `${model}, ${selectedColor}, ${length}cm`,
      unitPrice: total,
      discountedPrice: netPrice,
      quantity: profileQuantity,
      datasheet: selectedModel.datasheet || null,
    });

    showMessage(`${t('looseProfiles.messageProfileAdded')} · ${code} × ${profileQuantity}`);
    playSound();
  };

  const handleAddCover = () => {
    if (!selectedModel || !selectedCoverType || !coverLength) {
      showWarningMessage(t('looseProfiles.warningFillAllFields'));
      return;
    }

    if (coverQuantity < 1) {
      showWarningMessage(t('looseProfiles.warningValidCoverQty'));
      return;
    }

    const coverPrice = configData.coverPrices?.[selectedCoverType];
    if (coverPrice == null) {
      showWarningMessage(t('looseProfiles.warningCoverPriceUnavailable'));
      return;
    }

    const coverTotal = Math.round(coverPrice * (coverLength / 100) * 4.5);
    const coverNetPrice = roundCurrency(coverTotal * (1 - discount / 100));

    addToCart({
      code: `${selectedModel.name}-COVER`,
      description: `${t('looseProfiles.cover')} ${selectedCoverType}, ${t('looseProfiles.length')} ${coverLength} cm`,
      unitPrice: coverTotal,
      discountedPrice: coverNetPrice,
      quantity: coverQuantity,
    });

    showMessage(t('looseProfiles.messageCoverAdded'));
    playSound();
  };

  const handleAddClips = () => {
    if (!selectedModel) {
      showWarningMessage(t('looseProfiles.warningModelNotSelected'));
      return;
    }

    const pricePerUnit = configData.clipsPrices?.[selectedModel.name];
    if (!pricePerUnit || pricePerUnit <= 0) {
      showWarningMessage(t('looseProfiles.warningClipsUnavailable'));
      return;
    }

    if (clipsQuantity < 1) {
      showWarningMessage(t('looseProfiles.warningValidClipsQty'));
      return;
    }

    const clipsPrice = getClipsTotal(configData, selectedModel.name, 1, length);
    const clipsNetPrice = roundCurrency(clipsPrice * (1 - discount / 100));

    addToCart({
      code: `${selectedModel.name}-CLIPS`,
      description: `${t('looseProfiles.clips')} ${selectedModel.name}`,
      unitPrice: clipsPrice,
      discountedPrice: clipsNetPrice,
      quantity: clipsQuantity,
    });

    showMessage(t('looseProfiles.messageClipsAdded'));
    playSound();
  };

  const handleAddTappi = () => {
    if (!selectedModel) {
      showWarningMessage(t('looseProfiles.warningModelNotSelected'));
      return;
    }

    const pricePerUnit = configData.tappiPrices?.[selectedModel.name];
    if (!pricePerUnit || pricePerUnit <= 0) {
      showWarningMessage(t('looseProfiles.warningCapsUnavailable'));
      return;
    }

    if (tappiQuantity < 1) {
      showWarningMessage(t('looseProfiles.warningValidCapsQty'));
      return;
    }

    const tappiPrice = roundCurrency(pricePerUnit * 5);
    const tappiNetPrice = roundCurrency(tappiPrice * (1 - discount / 100));

    addToCart({
      code: `${selectedModel.name}-TAPPI`,
      description: `${t('looseProfiles.caps')} ${selectedModel.name}`,
      unitPrice: tappiPrice,
      discountedPrice: tappiNetPrice,
      quantity: tappiQuantity,
    });

    showMessage(t('looseProfiles.messageCapsAdded'));
    playSound();
  };

  const handleAddStrip = (strip) => {
    const stripQuantity = stripQuantities[strip.code] ?? 1;
    if (stripQuantity < 1) {
      return;
    }

    addToCart({
      code: strip.code,
      description: [strip.name, strip.power, strip.ledColor, strip.ipRating, strip.voltage, strip.pcbWidth, strip.coilLength]
        .filter(Boolean)
        .join(', '),
      unitPrice: strip.price,
      quantity: stripQuantity,
      datasheet: strip.datasheet || null,
    });

    showMessage(t('looseProfiles.messageStripAdded'));
    playSound();
  };

  const handleAddAccessory = (accessory) => {
    const quantity = accessoryQuantities[accessory.key] || 0;
    const listPrice = getAccessoryListPrice(accessory);
    if (!selectedModel || listPrice == null || quantity < 1) {
      return;
    }

    const colorCode = configData.profileColorCodes?.[selectedColor] || '';
    addToCart({
      code: `${configData.modelCodes?.[selectedModel.name] || selectedModel.name}-${accessory.key}${colorCode}`,
      description: `${accessory.name} ${selectedModel.name}, ${getColorLabel(selectedColor)}`,
      unitPrice: listPrice,
      discountedPrice: roundCurrency(listPrice * (1 - discount / 100)),
      quantity,
    });

    showMessage(t('looseProfiles.messageAccessoryAdded'));
    playSound();
  };

  const copyCode = () => {
    if (!pricing?.productCode) return;
    try {
      navigator.clipboard?.writeText(pricing.productCode).catch(() => {});
    } catch (e) {
      // copia non disponibile: il codice resta selezionabile a mano
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const jumpTo = (id) => {
    const el = stepRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('is-ping');
    setTimeout(() => el.classList.remove('is-ping'), 900);
  };

  // ---------- Elenco profili ----------
  if (view === 'list' || !selectedModel) {
    return (
      <div className="cat pc">
        <section className="cat-band">
          <div className="cat-band-in">
            <div className="cat-head">
              <div>
                <h1>{t('sidebar.looseProfiles')}</h1>
                <p>{t('lp.listSubtitle')}</p>
              </div>
              <span className="cat-count">{t('looseProfiles.profilesAvailable', { count: models.length })}</span>
            </div>
            <div className="cat-mounts pc-groups" role="group" aria-label={t('looseProfiles.profileType')}>
              {Object.entries(GROUP_LABELS).map(([value, label]) => (
                <button key={value} type="button" className="cat-mt" aria-pressed={group === value} onClick={() => setGroup(value)}>
                  <span>{label}</span>
                  <small>{groupCount(value)}</small>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="cat-grid-wrap">
          <div className="pc-list">
            {models.map((m) => {
              const startingNet = showPrices ? formatEuroPerMeter(getProfileStartingNetPricePerMeter(configData, m, discount)) : null;
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                  key={m.name}
                  className="pc-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectModel(m)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectModel(m); } }}
                >
                  <span className="pc-ph">
                    <img src={urlAsset(getProfileCardImage(m))} alt={m.name} loading="lazy" decoding="async" />
                    {/* Al passaggio del mouse la foto lascia il posto al disegno tecnico. */}
                    {m.techImage && (
                      <img src={urlAsset(m.techImage)} alt="" aria-hidden="true" className="is-tech" loading="lazy" decoding="async" />
                    )}
                  </span>
                  <span className="pc-body">
                    <span className="pc-grp">{m.groupLabel || GROUP_LABELS[m.groupKey]}</span>
                    <span className="pc-name">
                      <strong>{m.name}</strong>
                      {m.datasheet && (
                        <PdfButton label={t('configurator.datasheet')} onClick={(e) => { e.stopPropagation(); handleDownload(m.datasheet); }} />
                      )}
                    </span>
                    {startingNet && <span className="lp-from">{t('looseProfiles.fromNetPerMeter', { value: startingNet })}</span>}
                    <span className="pc-fin" title={m.availableFinishes?.map(getColorLabel).join(' / ')}>
                      {(m.availableFinishes || []).map((f) => <i key={f} style={{ background: FINISH_COLORS[f] || '#fff' }} />)}
                    </span>
                    <span className="pc-go">{t('looseProfiles.configure')} →</span>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  // ---------- Configurazione ----------
  const finishImage = getFinishImage(selectedModel, selectedColor);
  const shownImage = imageKind === 'tech' && selectedModel.techImage ? selectedModel.techImage : finishImage;
  const autoOk = (id) =>
    (id === 'finish' && availableFinishes.length <= 1) ||
    (id === 'length' && availableProfileLengths.length <= 1) ||
    (id === 'coverType' && availableCovers.length <= 1) ||
    (id === 'coverLength' && availableCoverLengths.length <= 1);
  const isOk = (id) => touched.has(id) || autoOk(id);
  const checks = [
    { id: 'finish', title: t('looseProfiles.finish'), value: getColorLabel(selectedColor), part: 'profile' },
    { id: 'length', title: t('looseProfiles.profileLength'), value: `${length} cm`, part: 'profile' },
    ...(hasCover ? [
      { id: 'coverType', title: t('looseProfiles.cover'), value: selectedCoverType, part: 'cover' },
      { id: 'coverLength', title: t('looseProfiles.coverLength'), value: `${coverLength} cm`, part: 'cover' },
    ] : []),
  ].map((c) => ({ ...c, ok: isOk(c.id) }));
  const todo = checks.filter((c) => !c.ok);
  const todoFor = (part) => todo.filter((c) => c.part === part);
  const confirmedCount = checks.length - todo.length;

  const tryAdd = (part, action) => {
    if (todoFor(part).length > 0 && askFor !== part) {
      setAskFor(part);
      return;
    }
    setAskFor(null);
    action();
  };

  const choiceHead = (id, title, value) => (
    <div className="pc-shd">
      <span className="pc-n">{isOk(id) ? '✓' : '•'}</span>
      <h3>{title}</h3>
      {!isOk(id) && <span className="pc-def">{t('pcfg.default')}</span>}
      <span className="pc-val">{value}</span>
    </div>
  );

  const askBox = (part, action) => (askFor === part && todoFor(part).length > 0 ? (
    <div className="pc-ask">
      <b>{todoFor(part).length === 1 ? t('pcfg.askOne') : t('pcfg.askMany', { n: todoFor(part).length })}</b>
      <ul>{todoFor(part).map((c) => <li key={c.id}>{c.title}: <strong>{c.value}</strong></li>)}</ul>
      <div className="pc-askr">
        <button type="button" className="is-check" onClick={() => jumpTo(todoFor(part)[0].id)}>{t('pcfg.check')}</button>
        <button type="button" className="is-anyway" onClick={() => { setAskFor(null); action(); }}>{t('pcfg.addAnyway')}</button>
      </div>
    </div>
  ) : null);

  const lineNet = (listTotal) => roundCurrency(listTotal * (1 - discount / 100));
  const profileUnitList = (() => {
    const perMeter = getProfileMaterialPricePerMeter(configData, selectedModel.name, selectedColor);
    return perMeter == null ? null : Math.round(perMeter * (length / 100) * 4.5);
  })();
  const coverUnitList = (() => {
    const perMeter = hasCover ? configData.coverPrices?.[selectedCoverType] : null;
    return perMeter == null ? null : Math.round(perMeter * (coverLength / 100) * 4.5);
  })();

  return (
    <div className="cat pc">
      <div className="pc-wrap">
        <div className="pc-head">
          <div>
            <div className="pc-crumb">{t('sidebar.looseProfiles')} · {selectedModel.groupLabel || GROUP_LABELS[selectedModel.groupKey] || ''}</div>
            <h1>{selectedModel.name}</h1>
          </div>
          <div className="pc-head-acts">
            <button type="button" className="pc-btn" onClick={handleBackToList}>
              <FiChevronLeft aria-hidden="true" /> {t('looseProfiles.backToProfiles')}
            </button>
          </div>
        </div>

        <div className="pc-grid">
          <aside className="pc-panel pc-gal">
            <button type="button" className="pc-hero" onClick={() => setZoomedImage(shownImage)} aria-label={t('looseProfiles.zoom')}>
              <img src={urlAsset(shownImage)} alt={selectedModel.name} />
              <span className="pc-zoomhint"><FiZoomIn aria-hidden="true" /></span>
            </button>
            <div className="pc-ths">
              <button type="button" className="pc-th" aria-pressed={imageKind !== 'tech'} onClick={() => setImageKind('photo')}>
                <img src={urlAsset(finishImage)} alt="" />
                {t('looseProfiles.productImage')} · {getColorLabel(selectedColor)}
              </button>
              {selectedModel.techImage && (
                <button type="button" className="pc-th" aria-pressed={imageKind === 'tech'} onClick={() => setImageKind('tech')}>
                  <img src={urlAsset(selectedModel.techImage)} alt="" />
                  {t('looseProfiles.technicalDrawing')}
                </button>
              )}
            </div>
          </aside>

          <div className="pc-steps">
            {/* 1. Profilo */}
            <section className="pc-panel pc-step lp-part">
              <div className="lp-parthead"><span className="lp-partn">1</span><h2>{t('lp.profilePart')}</h2></div>

              <div className={`lp-choice ${isOk('finish') ? 'is-ok' : 'is-def'}`} ref={(el) => { stepRefs.current.finish = el; }}>
                {choiceHead('finish', t('looseProfiles.finish'), getColorLabel(selectedColor))}
                <div className="pc-opts">
                  {availableFinishes.map((f) => (
                    <button key={f} type="button" className="pc-op" aria-pressed={selectedColor === f} onClick={() => { markTouched('finish'); setSelectedColor(f); setImageKind('photo'); }}>
                      <span className="pc-dot" style={{ background: FINISH_COLORS[f] || '#fff' }} />{getColorLabel(f)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`lp-choice ${isOk('length') ? 'is-ok' : 'is-def'}`} ref={(el) => { stepRefs.current.length = el; }}>
                {choiceHead('length', t('looseProfiles.profileLength'), `${length} cm`)}
                <div className="pc-opts">
                  {availableProfileLengths.map((len) => (
                    <button key={len} type="button" className="pc-op" aria-pressed={length === len} onClick={() => { markTouched('length'); setLength(len); }}>
                      {len} cm <small>{len / 100} m</small>
                    </button>
                  ))}
                </div>
              </div>

              {askBox('profile', handleAddProfile)}
              <div className="lp-addline">
                <span className="lp-addinfo">
                  <code>{generateLooseCode(configData, selectedModel.name, selectedColor, length, '')}</code>
                  {showPrices && profileUnitList != null && <span>{formatEuro(lineNet(profileUnitList))} <small>{t('lp.netEach')}</small></span>}
                </span>
                <QtyStepper value={profileQuantity} onChange={(v) => setProfileQuantity(Math.max(1, parseInt(v, 10) || 1))} label={t('looseProfiles.profileQty')} />
                <button type="button" className="pc-cta lp-add" onClick={() => tryAdd('profile', handleAddProfile)} disabled={!selectedColor || !length || profileQuantity < 1}>
                  <FiShoppingCart aria-hidden="true" /> {t('looseProfiles.addProfile')}
                </button>
              </div>
            </section>

            {/* 2. Copertura */}
            {hasCover && (
              <section className="pc-panel pc-step lp-part">
                <div className="lp-parthead"><span className="lp-partn">2</span><h2>{t('looseProfiles.cover')}</h2><span className="pc-val">{t('lp.coverHint')}</span></div>

                <div className={`lp-choice ${isOk('coverType') ? 'is-ok' : 'is-def'}`} ref={(el) => { stepRefs.current.coverType = el; }}>
                  {choiceHead('coverType', t('lp.coverType'), selectedCoverType)}
                  <div className="pc-opts">
                    {availableCovers.map((c) => (
                      <button key={c} type="button" className="pc-op" aria-pressed={selectedCoverType === c} disabled={availableCovers.length === 1} onClick={() => { markTouched('coverType'); setSelectedCoverType(c); }}>
                        {c}
                      </button>
                    ))}
                    {availableCovers.length === 1 && <span className="pc-lock">{t('pcfg.singleCover')}</span>}
                  </div>
                </div>

                <div className={`lp-choice ${isOk('coverLength') ? 'is-ok' : 'is-def'}`} ref={(el) => { stepRefs.current.coverLength = el; }}>
                  {choiceHead('coverLength', t('looseProfiles.coverLength'), `${coverLength} cm`)}
                  <div className="pc-opts">
                    {availableCoverLengths.map((len) => (
                      <button key={len} type="button" className="pc-op" aria-pressed={coverLength === len} onClick={() => { markTouched('coverLength'); setCoverLength(len); }}>
                        {len} cm <small>{len / 100} m</small>
                      </button>
                    ))}
                  </div>
                </div>

                {askBox('cover', handleAddCover)}
                <div className="lp-addline">
                  <span className="lp-addinfo">
                    <code>{selectedModel.name}-COVER</code>
                    {showPrices && coverUnitList != null && <span>{formatEuro(lineNet(coverUnitList))} <small>{t('lp.netEach')}</small></span>}
                  </span>
                  <QtyStepper value={coverQuantity} onChange={(v) => setCoverQuantity(Math.max(1, parseInt(v, 10) || 1))} label={t('looseProfiles.coverQty')} />
                  <button type="button" className="pc-cta lp-add" onClick={() => tryAdd('cover', handleAddCover)} disabled={!selectedCoverType || !coverLength || coverQuantity < 1}>
                    <FiShoppingCart aria-hidden="true" /> {t('looseProfiles.addCover')}
                  </button>
                </div>
              </section>
            )}

            {/* Strip compatibili */}
            {compatibleStrips.length > 0 && (
              <section className="pc-panel pc-step lp-part">
                <div className="lp-parthead"><span className="lp-partn">+</span><h2>{t('looseProfiles.compatibleStrip')}</h2><span className="pc-val">{t('looseProfiles.compatibleStripIntro')}</span></div>
                {compatibleStrips.map((strip) => (
                  <div key={strip.code} className="lp-strip">
                    <span className="lp-addinfo">
                      <b>{strip.name}</b>
                      <small>{[strip.code, strip.power, strip.ledColor, strip.ipRating, strip.voltage, strip.pcbWidth, strip.coilLength].filter(Boolean).join(' · ')}</small>
                      {showPrices && <span>{t('stripLed.price')} <s>{formatEuro(strip.price)}</s> · {formatEuro(strip.price * (1 - discount / 100))} <small>{t('lp.netEach')}</small></span>}
                    </span>
                    <QtyStepper value={stripQuantities[strip.code] ?? 1} onChange={(v) => setStripQuantities((q) => ({ ...q, [strip.code]: Math.max(1, parseInt(v, 10) || 1) }))} label={t('looseProfiles.stripQty')} />
                    <button type="button" className="pc-cta lp-add" onClick={() => handleAddStrip(strip)}>
                      <FiShoppingCart aria-hidden="true" /> {t('looseProfiles.addStrip')}
                    </button>
                  </div>
                ))}
              </section>
            )}

            {/* Accessori: clips, tappi, accessori del modello */}
            {(clipsAvailable || tappiAvailable || modelAccessories.length > 0) && (
              <section className="pc-panel pc-step lp-part" ref={(el) => { stepRefs.current.accessories = el; }}>
                <div className="lp-parthead"><span className="lp-partn">+</span><h2>{t('looseProfiles.accessories')}</h2><span className="pc-val">{t('looseProfiles.accessoriesIntro')}</span></div>
                <div className="pc-acc">
                  {clipsAvailable && (
                    <div className="pc-accit">
                      {selectedModel.clipsImage && (
                        <button type="button" className="pc-accimg" onClick={() => setZoomedImage(selectedModel.clipsImage)} aria-label={t('looseProfiles.zoom')}>
                          <img src={urlAsset(selectedModel.clipsImage)} alt={t('looseProfiles.clips')} />
                        </button>
                      )}
                      <b>{t('looseProfiles.clips')}</b>
                      {showPrices && <span className="pc-accp">{formatEuro(getClipsTotal(configData, selectedModel.name, 1, length))}</span>}
                      <div className="pc-accr">
                        <input type="number" min="0" value={clipsQuantity} aria-label={t('looseProfiles.clips')} onChange={(e) => setClipsQuantity(Math.max(0, parseInt(e.target.value, 10) || 0))} />
                        <button type="button" onClick={handleAddClips} disabled={clipsQuantity < 1}>{t('looseProfiles.addClips')}</button>
                      </div>
                    </div>
                  )}
                  {tappiAvailable && (
                    <div className="pc-accit">
                      <b>{t('looseProfiles.caps')}</b>
                      {showPrices && <span className="pc-accp">{formatEuro(roundCurrency(configData.tappiPrices[selectedModel.name] * 5))}</span>}
                      <div className="pc-accr">
                        <input type="number" min="0" value={tappiQuantity} aria-label={t('looseProfiles.caps')} onChange={(e) => setTappiQuantity(Math.max(0, parseInt(e.target.value, 10) || 0))} />
                        <button type="button" onClick={handleAddTappi} disabled={tappiQuantity < 1}>{t('looseProfiles.addCaps')}</button>
                      </div>
                    </div>
                  )}
                  {modelAccessories.map((accessory) => {
                    const listPrice = getAccessoryListPrice(accessory);
                    const quantity = accessoryQuantities[accessory.key] || 0;
                    return (
                      <div key={accessory.key} className="pc-accit">
                        {accessory.image && (
                          <button type="button" className="pc-accimg" onClick={() => setZoomedImage(accessory.image)} aria-label={t('looseProfiles.zoom')}>
                            <img src={urlAsset(accessory.image)} alt={accessory.name} />
                          </button>
                        )}
                        <b>{accessory.name}</b>
                        {showPrices && (
                          <span className="pc-accp">
                            {listPrice == null ? t('looseProfiles.priceToBeDefined') : `${formatEuro(listPrice)} · ${getColorLabel(selectedColor)}`}
                          </span>
                        )}
                        <div className="pc-accr">
                          <input
                            type="number"
                            min="0"
                            value={quantity}
                            aria-label={accessory.name}
                            onChange={(e) => setAccessoryQuantities((prev) => ({ ...prev, [accessory.key]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                          />
                          <button type="button" onClick={() => handleAddAccessory(accessory)} disabled={listPrice == null || quantity < 1}>
                            {t('looseProfiles.addAccessory')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          <aside className="pc-panel pc-sum">
            <h3>{t('looseProfiles.summary')}</h3>

            {checks.length > 0 && (
              <div className={`pc-prog${todo.length ? '' : ' is-all'}`}>
                <div className="pc-pt">
                  <b>{t('pcfg.confirmedOf', { n: confirmedCount, total: checks.length })}</b>
                  {!todo.length && <span>{t('pcfg.allChecked')}</span>}
                </div>
                <div className="pc-pb"><i style={{ width: `${(confirmedCount / checks.length) * 100}%` }} /></div>
                {todo.length > 0 && (
                  <div className="pc-pl">
                    {t('pcfg.toCheck')}
                    {todo.map((c) => <button key={c.id} type="button" onClick={() => jumpTo(c.id)}>{c.title}</button>)}
                  </div>
                )}
              </div>
            )}

            {pricing ? (
              <>
                <div className="pc-code">
                  <span>{t('looseProfiles.code')}</span>
                  <code>{pricing.productCode}</code>
                  <button type="button" className="pc-copy" onClick={copyCode}>
                    <FiCopy aria-hidden="true" /> {copied ? t('pcfg.copied') : t('pcfg.copy')}
                  </button>
                </div>

                <div className="pc-rows">
                  <div><span>{t('looseProfiles.profileQuantity')}</span><b>{profileQuantity} × {length} cm</b></div>
                  {showPrices && <div><span>{t('looseProfiles.netProfile')}</span><b>{formatEuro(pricing.profileNetPrice)}</b></div>}
                  {hasCover && <div><span>{t('looseProfiles.cover')}</span><b>{coverQuantity} × {coverLength} cm</b></div>}
                  {hasCover && showPrices && <div><span>{t('looseProfiles.netCover')}</span><b>{formatEuro(pricing.coverNetPrice)}</b></div>}
                  {tappiAvailable && <div><span>{t('looseProfiles.caps')}</span><b>{tappiQuantity}{showPrices ? ` · ${formatEuro(pricing.tappiNetPrice)}` : ''}</b></div>}
                  {clipsAvailable && <div><span>{t('looseProfiles.clips')}</span><b>{clipsQuantity}{showPrices ? ` · ${formatEuro(pricing.clipsNetPrice)}` : ''}</b></div>}
                </div>

                {showPrices ? (
                  <div className="pc-pbox">
                    <span className="pc-l">{t('looseProfiles.totalList')} <s>{formatEuro(pricing.grandTotal)}</s></span>
                    <span className="pc-net">
                      {formatEuro(pricing.grandNetTotal)}
                      {discount > 0 && <small>−{discount}%</small>}
                    </span>
                    <span className="pc-w">{t('lp.totalHint')}</span>
                  </div>
                ) : (
                  <div className="pc-pbox"><span className="pc-w">{t('looseProfiles.prices')}: {t('common.hidden')}</span></div>
                )}
              </>
            ) : (
              <p className="pc-w">{t('looseProfiles.noProfileSummary')}</p>
            )}

            {warningMessage ? <div className="pc-warn">{warningMessage}</div> : null}

            <button type="button" className="pc-btn pc-gocart" onClick={() => navigate('/cart')}>
              {t('looseProfiles.cartWithCount', { count: cart.length })}
            </button>
          </aside>
        </div>
      </div>

      {zoomedImage && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="cat-zoom" onClick={() => setZoomedImage(null)}>
          <img src={urlAsset(zoomedImage)} alt="Zoom" />
        </div>
      )}

      <AddedToast message={successMessage} onGoToCart={() => navigate('/cart')} goToCartLabel={t('shop.goToCart')} />
    </div>
  );
};

export default LooseProfilesConfigurator;
